/**
 * DecisionModal — the four-decision approver dialog used by D4's
 * ApprovalWorkspace timeline. Replaces the legacy ApprovalDecideForm
 * (single Select dropdown, optional comments, no delegatee picker).
 *
 * Decisions:
 *   - APPROVED            comments optional, advances chain to next sequence
 *   - REJECTED            comments REQUIRED, terminates chain
 *   - CHANGES_REQUESTED   comments REQUIRED, resets chain to IN_ANALYSIS
 *   - DELEGATED           comments REQUIRED + delegatee REQUIRED, soft-deletes
 *                          original row + creates new PENDING row at the same
 *                          sequence with `delegatedFrom` set to the original
 *                          approver
 *
 * D1's approvalService enforces these requirements server-side; this modal
 * mirrors them client-side so the user sees the requirement before submitting.
 *
 * Linear: MYD-204 (D5).
 */

import { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from 'components/ui/dialog'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import {
	CheckCircle2,
	XCircle,
	AlertCircle,
	Forward,
	Loader2,
} from 'lucide-react'
import { cn } from 'lib/utils'
import { recordDecision } from 'slices/approvalSlice'
import { fetchEvaluationById } from 'slices/evaluationSlice'

import ApproverPicker, { type ApproverUser } from './ApproverPicker'

type Decision = 'APPROVED' | 'REJECTED' | 'CHANGES_REQUESTED' | 'DELEGATED'

interface DecisionConfig {
	value: Decision
	label: string
	icon: typeof CheckCircle2
	activeClass: string
	hint: string
}

const DECISIONS: DecisionConfig[] = [
	{
		value: 'APPROVED',
		label: 'Approve',
		icon: CheckCircle2,
		activeClass: 'bg-emerald-600 text-white border-emerald-600',
		hint: 'Advances the chain to the next approver. Comments optional.',
	},
	{
		value: 'REJECTED',
		label: 'Reject',
		icon: XCircle,
		activeClass: 'bg-red-600 text-white border-red-600',
		hint: 'Terminates the chain — evaluation status becomes REJECTED. Comments required.',
	},
	{
		value: 'CHANGES_REQUESTED',
		label: 'Request changes',
		icon: AlertCircle,
		activeClass: 'bg-amber-500 text-white border-amber-500',
		hint: 'Bounces the evaluation back to IN_ANALYSIS so the owner can address feedback. Comments required.',
	},
	{
		value: 'DELEGATED',
		label: 'Delegate',
		icon: Forward,
		activeClass: 'bg-blue-600 text-white border-blue-600',
		hint: 'Hands the slot to another MDT user at the same sequence. Comments + delegatee required.',
	},
]

interface DecisionModalProps {
	open: boolean
	onClose: () => void
	evalId: string
	approval: any
}

const DecisionModal = ({ open, onClose, evalId, approval }: DecisionModalProps) => {
	const dispatch = useDispatch()
	const [decision, setDecision] = useState<Decision>('APPROVED')
	const [comments, setComments] = useState('')
	const [delegatee, setDelegatee] = useState<ApproverUser | null>(null)
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// Reset when the modal re-opens or the target approval changes.
	useEffect(() => {
		if (open) {
			setDecision('APPROVED')
			setComments('')
			setDelegatee(null)
			setSubmitting(false)
			setError(null)
		}
	}, [open, approval?._id])

	const config = DECISIONS.find((d) => d.value === decision)!

	const trimmedComments = comments.trim()
	const commentsRequired = decision !== 'APPROVED'
	const delegateeRequired = decision === 'DELEGATED'

	const validationError = (() => {
		if (commentsRequired && !trimmedComments) {
			return 'Comments are required for this decision.'
		}
		if (delegateeRequired && !delegatee) {
			return 'Pick a delegatee before submitting.'
		}
		return null
	})()

	const handleSubmit = useCallback(async () => {
		if (validationError) {
			setError(validationError)
			return
		}
		setError(null)
		setSubmitting(true)
		// @ts-ignore — thunk
		const result: any = await dispatch(
			recordDecision({
				evalId,
				approvalId: approval._id,
				decision,
				comments: trimmedComments || undefined,
				delegateeId: delegateeRequired ? delegatee!._id : undefined,
			}),
		)
		setSubmitting(false)
		if (result?.error) {
			setError(result.payload || 'Failed to record decision')
			return
		}
		// Status may have changed (APPROVED final / REJECTED / CHANGES_REQUESTED) —
		// refresh evaluation so outlet context reflects the new status.
		// @ts-ignore — thunk
		dispatch(fetchEvaluationById(evalId))
		onClose()
	}, [
		dispatch,
		evalId,
		approval,
		decision,
		trimmedComments,
		delegatee,
		delegateeRequired,
		validationError,
		onClose,
	])

	if (!approval) return null

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<div className="min-w-0">
						<DialogTitle>Record decision</DialogTitle>
						<DialogDescription>
							You are deciding as approver #{approval.sequence}. Your choice may finalize
							the chain or bounce the evaluation back to the owner.
						</DialogDescription>
					</div>
				</DialogHeader>

				<div className="px-5 py-4 space-y-4">
					{/* Decision selector */}
					<div>
						<Label className="mb-1.5 block">Decision</Label>
						<div className="grid grid-cols-2 gap-2">
							{DECISIONS.map((d) => {
								const Icon = d.icon
								const active = decision === d.value
								return (
									<button
										key={d.value}
										type="button"
										onClick={() => setDecision(d.value)}
										className={cn(
											'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border transition-colors',
											active
												? d.activeClass
												: 'bg-white text-gray-700 border-gray-200 hover:border-gray-300',
										)}
									>
										<Icon className="w-4 h-4" />
										{d.label}
									</button>
								)
							})}
						</div>
						<p className="text-xs text-gray-500 mt-1.5">{config.hint}</p>
					</div>

					{/* Comments */}
					<div>
						<Label htmlFor="dmComments" className="mb-1.5 block">
							Comments {commentsRequired ? '*' : '(optional)'}
						</Label>
						<textarea
							id="dmComments"
							value={comments}
							onChange={(e) => setComments(e.target.value)}
							placeholder={
								commentsRequired
									? 'Required — explain your decision so the owner has context.'
									: 'Optional notes for the record.'
							}
							rows={4}
							className={cn(
								'w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0080fe]/20 focus:border-[#0080fe]',
								commentsRequired && !trimmedComments
									? 'border-amber-300'
									: 'border-gray-200',
							)}
						/>
					</div>

					{/* Delegatee picker (only when DELEGATED) */}
					{delegateeRequired && (
						<div>
							<Label className="mb-1.5 block">Delegatee *</Label>
							{delegatee ? (
								<div className="flex items-center gap-2 px-3 py-2 rounded-md border border-blue-200 bg-blue-50">
									<div className="w-7 h-7 rounded-full bg-blue-200 text-blue-700 flex items-center justify-center shrink-0 text-[10px] font-semibold uppercase">
										{(delegatee.name || delegatee.email || '?').slice(0, 1)}
									</div>
									<div className="flex-1 min-w-0">
										<div className="font-medium text-sm text-gray-900 truncate">
											{delegatee.name || delegatee.email}
										</div>
										{delegatee.email && (
											<div className="text-xs text-gray-500 truncate">
												{delegatee.title ? `${delegatee.title} · ` : ''}
												{delegatee.email}
											</div>
										)}
									</div>
									<button
										type="button"
										onClick={() => setDelegatee(null)}
										className="p-1.5 rounded text-blue-600 hover:bg-blue-100 shrink-0 text-xs"
									>
										Change
									</button>
								</div>
							) : (
								<ApproverPicker
									excludeIds={
										// Exclude the current approver — can't delegate to oneself.
										[
											typeof approval.approver === 'object'
												? approval.approver?._id
												: String(approval.approver),
										].filter(Boolean) as string[]
									}
									onPick={setDelegatee}
								/>
							)}
							<p className="text-xs text-gray-500 mt-1.5">
								The delegatee replaces you at sequence #{approval.sequence}. The
								original row is preserved with status DELEGATED for the audit trail.
							</p>
						</div>
					)}

					{error && (
						<div className="flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
							<AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
							<span>{error}</span>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="ghost" onClick={onClose} disabled={submitting}>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={handleSubmit}
						disabled={submitting || Boolean(validationError)}
						className="gap-1.5"
					>
						{submitting ? (
							<>
								<Loader2 className="w-4 h-4 animate-spin" />
								Recording…
							</>
						) : (
							<>
								<config.icon className="w-4 h-4" />
								Confirm {config.label.toLowerCase()}
							</>
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default DecisionModal
